/**
 * Типы ответов API, которыми пользуются клиент и экраны админки.
 * Определены здесь, а не переиспользованы из `@rk/module-http`/`@rk/contracts`:
 * админка собирается отдельным бандлом esbuild и не должна тянуть за собой
 * серверные пакеты — только форму JSON, которую реально отдаёт HTTP.
 */

export interface PageRequest {
  limit?: number
  cursor?: string
}

export interface Page<T> {
  items: T[]
  /** Курсор следующей страницы; null — страниц больше нет. */
  nextCursor: string | null
}

/** Канал номера. Тот же закрытый список, что и в контрактах платформы. */
export type ChannelProvider = 'whatsapp' | 'telegram'

/**
 * Шаг входа, поданный человеком. Тот же набор, что принимает API:
 * последовательность вопросов определяет провайдер, а портал лишь
 * отвечает на заданный.
 */
export type AuthStep =
  | { kind: 'phone'; phone: string }
  | { kind: 'code'; code: string }
  | { kind: 'password'; password: string }
  | { kind: 'qr' }

export interface Instance {
  id: string
  name: string
  provider: ChannelProvider
  status: string
  phoneNumber: string | null
  lastError: string | null
  createdAt: string
  updatedAt: string
}

export interface QrCode {
  value: string
  expiresAt: string
  /** Готовое изображение (data URL) — сканировать нужно картинку, не строку. */
  image: string
}

export interface Webhook {
  id: string
  url: string
  /**
   * Инстанс подписки или null — «все инстансы». Подписка на инстанс
   * получает только его уведомления: у арендатора с десятком номеров
   * общая подписка означает десятикратный поток.
   */
  instanceId: string | null
  events: string[]
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface CreatedWebhook extends Webhook {
  /** Подписной ключ. Отдаётся один раз при создании и больше нигде. */
  secret: string
}

export interface Delivery {
  id: string
  event: string
  status: 'pending' | 'delivered' | 'failed'
  attempts: number
  responseStatus: number | null
  lastError: string | null
  createdAt: string
  updatedAt: string
}

export interface Chat {
  id: string
  providerId: string
  title: string | null
  isGroup: boolean
  lastAt: string | null
  createdAt: string
}

export interface MessageMedia {
  id: string
  /** document | image | video | audio | sticker. */
  kind: string
  /** Голосовое, а не присланный музыкальный файл. */
  voice: boolean
  fileName: string
  mimeType: string
  sizeBytes: number
  durationSeconds: number | null
}

export interface Message {
  id: string
  direction: 'in' | 'out'
  body: string | null
  /** Дублирует `media.id`: поле было в опубликованном API версии 2.1.0. */
  mediaId: string | null
  media: MessageMedia | null
  status: string
  at: string
}

export interface Contact {
  id: string
  phone: string
  name: string | null
  nameSource: 'channel' | 'manual'
}

export interface User {
  id: string
  email: string
  tenantId: string | null
  disabledAt: string | null
  createdAt: string
}

export interface Role {
  id: string
  tenantId: string | null
  name: string
  permissions: string[]
}

export interface ApiKey {
  id: string
  name: string
  /** Опознавательный отрезок ключа. Полного значения здесь нет — оно не хранится. */
  prefix: string
  scopes: string[]
  expiresAt: string | null
  revokedAt: string | null
}

export interface CreatedApiKey extends ApiKey {
  /** Полный ключ. Присутствует только в ответе на создание и ротацию. */
  key: string
}

/**
 * Замечание валидатора. `nodeId` пуст у замечаний про граф целиком
 * (например, отсутствующий триггер): подсветить их на узле нельзя, и
 * показываются они списком.
 */
export interface ValidationIssue {
  nodeId: string | null
  field: string | null
  message: string
  code: string
}

/** Шаг прогона с сохранёнными входом и выходом (FR-UI-005). */
export interface NodeRun {
  id: string
  nodeId: string
  runIndex: number
  attempt: number
  status: string
  input: unknown
  output: unknown
  error: string | null
  startedAt: string | null
  finishedAt: string | null
}

export interface Execution {
  id: string
  versionId: string
  mode: string
  status: string
  error: string | null
  startedAt: string
  stoppedAt: string | null
}

export interface ExecutionDetail extends Execution {
  trigger: unknown
  replayOf: { executionId: string; nodeRunId: string } | null
  nodeRuns: NodeRun[]
}

export interface Pipeline {
  id: string
  name: string
  description: string | null
  status: string
  /** Почему сценарий помечен сломанным: показывается как есть (FR-PIPE-016). */
  brokenReason: string | null
  currentVersionId: string | null
  updatedAt: string
}

export interface PipelineDetail extends Pipeline {
  draftGraph: unknown
  draftUpdatedAt: string | null
}

/**
 * Credential без секрета. Поля с данными здесь нет и быть не может: сервер
 * его не отдаёт (FR-CRED-001), и заводить для него место на клиенте значит
 * заводить место, куда он однажды попадёт.
 */
export interface Credential {
  id: string
  type: string
  name: string
  createdAt: string
  updatedAt: string
}

export interface CredentialFieldDescriptor {
  name: string
  displayName: string
  /** Значение — секрет: поле скрыто и стирается после отправки (FR-UI-009). */
  secret: boolean
  required: boolean
  description?: string
}

/**
 * Описание типа credential для формы. Приходит от службы: список полей,
 * зашитый в админку, расходится со схемой проверки — и человек узнаёт об
 * этом отказом при создании.
 */
export interface CredentialTypeDescriptor {
  type: string
  displayName: string
  /** Набор полей произвольный (имя-значение), как у произвольных заголовков. */
  freeForm: boolean
  fields: CredentialFieldDescriptor[]
}

/**
 * Переменная арендатора. Значение отдаётся как есть: это не секрет, а
 * настройка вроде адреса внешней службы — прятать её значит мешать
 * проверить, что подставится в выражение. Секреты живут в credentials.
 */
export interface Variable {
  key: string
  value: string
  updatedBy: string
  createdAt: string
  updatedAt: string
}

/**
 * Опубликованная версия сценария вместе с её графом. Граф отдаётся целиком:
 * сравнение версий — чистая функция от двух графов, и считать разность на
 * клиенте дешевле, чем заводить у службы второе определение слова
 * «изменилось» (FR-UI-007).
 */
export interface PipelineVersion {
  id: string
  number: number
  graph: unknown
  publishedAt: string
  publishedBy: string
}

/** Прогон завершён и больше не изменится: опрашивать его дальше незачем. */
export const TERMINAL_EXECUTION_STATUSES: readonly string[] = ['success', 'failed', 'cancelled']

/** Пара токенов, которую сервер выдаёт при входе и обновляет при `/auth/refresh`. */
export interface TokenSet {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

/** Данные сессии: токены плюс права обратившегося (FR-ADM-007 их читает). */
export interface Session extends TokenSet {
  permissions: string[]
}

/**
 * Отказ сервера, дошедший до вызывающего экрана как есть — сообщение
 * написано по-русски и объясняет причину лучше, чем код статуса (FR-ADM-006).
 */
export class ApiClientError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
  }
}
